import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'react-feather';

import { useCategories } from '@/Product/hooks/useCategory';
import {
  CategoryDropdownProps,
  CategoryItem,
} from '@/Product/types/productTypes';

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  selectedCategory,
  onCategoryChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set(),
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: categoryResponse, isLoading, error } = useCategories();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleExpanded = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategorySelect = (category: CategoryItem) => {
    // children이 없거나 빈 배열인 경우에만 선택 가능
    if (!category.children || category.children.length === 0) {
      onCategoryChange({
        categoryId: category.id,
        categoryName: category.category,
      });
      setIsOpen(false);
      setExpandedCategories(new Set());
    }
  };

  const renderCategoryItem = (category: CategoryItem, level: number = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const paddingLeft = level * 16 + 12;

    let backgroundColor = '';
    if (level === 0) backgroundColor = 'bg-gray-50';
    else if (level === 1) backgroundColor = 'bg-gray-25';
    else backgroundColor = 'bg-white';

    return (
      <div key={category.id}>
        <div
          tabIndex={0}
          role="button"
          className={`flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-gray-100 ${backgroundColor}`}
          style={{ paddingLeft: `${paddingLeft}px` }}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(category.id);
            } else {
              handleCategorySelect(category);
            }
          }}
        >
          <span
            className={`text-sm ${level === 0 ? 'font-medium' : 'font-normal'}`}
          >
            {category.category}
          </span>
          {hasChildren && (
            <div className="ml-2">
              {isExpanded ? (
                <ChevronDown size={16} className="text-gray-500" />
              ) : (
                <ChevronRight size={16} className="text-gray-500" />
              )}
            </div>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {category.children.map((child) =>
              renderCategoryItem(child, level + 1),
            )}
          </div>
        )}
      </div>
    );
  };

  if (error) {
    return (
      <div className="relative">
        <div className="h-10 w-full rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
          카테고리 로드 실패
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        tabIndex={0}
        role="button"
        className={`relative h-10 w-full rounded border border-[#CECECE] px-3 text-sm ${
          disabled
            ? 'cursor-not-allowed bg-gray-100'
            : 'cursor-pointer bg-white'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          type="text"
          className="h-full w-full cursor-pointer border-none bg-transparent outline-none"
          value={selectedCategory.categoryName || ''}
          placeholder="Click to add category"
          readOnly
          disabled={disabled}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-500" />
          ) : (
            <ChevronDown
              size={16}
              className={`text-gray-500 transition-transform ${
                isOpen ? 'rotate-180 transform' : ''
              }`}
            />
          )}
        </div>
      </div>

      {isOpen && !disabled && categoryResponse?.data && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded border border-[#CECECE] bg-white shadow-lg">
          {categoryResponse.data.map((category) =>
            renderCategoryItem(category),
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryDropdown;
